/**
 * Tutor personal-account form section.
 * Identity / contact (create vs edit), address, and professional situation.
 */
import type { ChangeEvent, ReactNode } from 'react';
import { IdCard, KeyRound, Mail, User } from 'lucide-react';
import { Input } from '@/components/forms/Input';
import { RadioGroup } from '@/components/forms/RadioGroup';
import { Select } from '@/components/forms/Select';
import { PhoneInput } from '@/components/forms/PhoneInput';
import { DateSplitInput } from '@/components/forms/DateSplitInput';
import { AddressAutocompleteInput } from '@/components/forms/AddressAutocompleteInput';
import { SmartContactInput } from '@/components/forms/SmartContactInput';
import type { StoredContact } from '@/components/forms/SmartContactInput';
import { ProfessionalSituationFields } from '@/components/common/ProfessionalSituationFields';
import {
  BOOLEAN_SELECT_OPTIONS,
  GENDER_OPTIONS_WITHOUT_OTHER,
  TUTOR_ACCOUNT_STATUS_CONFIG,
} from '@/components/tutors/tutor-configs';
import {
  applyRemoteTutorAddressSelection,
  getRemoteTutorMaxBirthDate,
} from '@/components/tutors/remote/tutor-form-utils';
import {
  writeUnifiedContactSwap,
  booleanRadioValue,
  canSwapPrimaryContact,
  contactExistsErrorMessage,
  oppositeContactType,
  primaryContactFieldError,
} from '@/utils/user-contact-utils';
import { Button } from '@/components/forms/Button';

/** Props for {@link TutorPersonalAccountSection}. */
type Props = Readonly<{
  formData: Record<string, any>;
  onChange: (key: string, value: any) => void;
  onBlur?: (field: string) => void;
  validation?: any;
  mode: 'create' | 'edit';
  defaultCountry: string;
  /** Edit mode only: userId for action buttons */
  userId?: string;
  /** Edit mode only: emailVerified status */
  emailVerified?: boolean;
  /** Contact exists check result */
  contactExists?: boolean | null;
  /** Secondary contact exists check result (create mode only) */
  secondaryContactExists?: boolean | null;
  /** Edit mode: primary contact exists */
  primaryContactExists?: boolean | null;
  /** Edit mode: primary and secondary are identical */
  contactsAreIdentical?: boolean;
  /** Edit mode: server email for dirty check */
  serverEmail?: string | null;
  /** Edit mode: server phone for dirty check */
  serverPhone?: string | null;
  /** Edit mode: server primary kind for dirty check */
  serverPrimaryKind?: 'email' | 'phone' | null;
}>;

/** Tutor personal-account form section. */
export function TutorPersonalAccountSection({
  formData,
  onChange,
  onBlur,
  validation,
  mode,
  defaultCountry,
  userId,
  emailVerified,
  contactExists,
  secondaryContactExists,
  primaryContactExists,
  contactsAreIdentical,
  serverEmail,
  serverPhone,
  serverPrimaryKind,
}: Props) {
  const isEdit = mode === 'edit';
  const effectivePrimaryExists = isEdit ? primaryContactExists : contactExists;
  const contactExistsError = primaryContactFieldError(
    effectivePrimaryExists,
    formData.contactType,
    !!contactsAreIdentical,
  );
  const secondaryContactType = oppositeContactType(formData.contactType);
  const secondaryContactExistsError = contactExistsErrorMessage(
    isEdit ? contactExists : secondaryContactExists,
    secondaryContactType,
  );
  const contactFormatError = validation?.getFieldError('contact') as string | undefined;
  const secondaryFormatError = validation?.getFieldError('secondaryContact') as string | undefined;
  const contactFieldError = contactExistsError ?? contactFormatError;
  const secondaryFieldError = secondaryContactExistsError ?? secondaryFormatError;

  /** Persist primary contact and inferred type. */
  function handleContactChange(value: string, type: string, countryCode?: string): void {
    onChange('contact', value);
    onChange('contactType', type);
    if (countryCode) onChange('contactCountryCode', countryCode);
  }

  /** Swap primary and secondary contact fields. */
  function handleSwapContacts(): void {
    writeUnifiedContactSwap(onChange, formData, defaultCountry);
  }

  /** Swap beside the primary field only while the primary is a real email or phone. */
  const swapButton =
    isEdit && canSwapPrimaryContact(formData.contactType) ? (
      <Button type="button" preset="contact-swap" size="md" onClick={handleSwapContacts} />
    ) : undefined;

  /** storedContact with live primaryKind so the field mode syncs after a swap. */
  const liveStoredContact: StoredContact | undefined = isEdit
    ? {
        primaryKind: formData.contactType as 'email' | 'phone',
        serverPrimaryKind,
        email: serverEmail,
        phone: serverPhone,
      }
    : undefined;

  return (
    <div className="form-section">
      <h4 className="form-section-title">
        <IdCard className="w-4 h-4" />
        Compte et informations personnelles
      </h4>

      <div className="form-section">
        <h4 className="form-section-title">
          <KeyRound className="w-4 h-4" />
          Compte
        </h4>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Input
            label="Prénom"
            value={formData.firstName ?? ''}
            onChange={(e) => onChange('firstName', e.target.value)}
            required
            inputSize="sm"
            placeholder="Ex: Marie"
          />
          <Input
            label="Nom"
            value={formData.lastName ?? ''}
            onChange={(e) => onChange('lastName', e.target.value)}
            required
            inputSize="sm"
            placeholder="Ex: Martin"
          />
          <Select
            label="Genre"
            value={formData.gender ?? ''}
            onChange={(val) => onChange('gender', val)}
            options={[...GENDER_OPTIONS_WITHOUT_OTHER]}
            required
            size="sm"
            placeholder="Sélectionner"
          />
        </div>
        {formData.status === 'archived' ? (
          <Input
            label="Statut"
            value={TUTOR_ACCOUNT_STATUS_CONFIG.archived.label}
            disabled
            inputSize="sm"
          />
        ) : null}
        <div className="grid grid-cols-2 items-start gap-2 mt-4">
          <SmartContactInput
            label="Contact principal"
            value={formData.contact ?? ''}
            onContactChange={handleContactChange}
            required
            inputSize="sm"
            storedContact={liveStoredContact}
            showContactActions={isEdit}
            emailVerified={emailVerified}
            userId={userId}
            state={contactFieldError ? 'error' : undefined}
            error={contactFieldError}
            swapButton={swapButton}
          />
          {renderRemoteTutorSecondaryContact({
            contactType: formData.contactType,
            secondaryContact: formData.secondaryContact ?? '',
            countryCode: formData.secondaryContactCountryCode ?? defaultCountry,
            fieldError: secondaryFieldError,
            onChange,
            onBlur,
          })}
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 items-start mt-4">
          <RadioGroup
            className="sm:col-span-2"
            name="acceptSharePhone"
            label="Partager ses coordonnées avec les familles d'élèves"
            required
            layout="horizontal"
            options={BOOLEAN_SELECT_OPTIONS}
            value={booleanRadioValue(formData.acceptSharePhone)}
            onValueChange={(v) => onChange('acceptSharePhone', v === 'true')}
          />
        </div>
      </div>

      {/* Qualification step exists in the backend but has no form fields. Left empty to keep step structure. */}

      <div className="form-section">
        <h4 className="form-section-title">
          <User className="w-4 h-4" />
          Informations personnelles
        </h4>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <AddressAutocompleteInput
            label="Adresse"
            value={formData.address ?? ''}
            onChange={(value) => onChange('address', value)}
            onSelection={(result) => applyRemoteTutorAddressSelection(onChange, result)}
            size="sm"
          />
          <Input
            label="Code postal"
            value={formData.zipCode ?? ''}
            onChange={(e) => onChange('zipCode', e.target.value)}
            inputSize="sm"
            placeholder="Ex: 75001"
          />
          <Input
            label="Ville"
            value={formData.city ?? ''}
            onChange={(e) => onChange('city', e.target.value)}
            inputSize="sm"
            placeholder="Ex: Paris"
          />
          <Input
            label="Pays"
            value={formData.country ?? ''}
            onChange={(e) => onChange('country', e.target.value)}
            inputSize="sm"
            placeholder="Ex: France"
          />
          <DateSplitInput
            label="Date de naissance"
            value={formData.dateOfBirth ?? ''}
            onChange={(value) => onChange('dateOfBirth', value)}
            onBlur={() => onBlur?.('dateOfBirth')}
            maxDate={getRemoteTutorMaxBirthDate()}
            error={validation?.getFieldError('dateOfBirth')}
            state={validation?.getFieldState('dateOfBirth')}
            size="sm"
          />
        </div>

        <ProfessionalSituationFields formData={formData} onChange={onChange} persona="tutor" />

        <div className="mt-4">
          <RadioGroup
            name="isParticipatedToStudies"
            label="Souhaite être contacté(e) pour partager son expérience de mentor ?"
            required
            layout="horizontal"
            options={BOOLEAN_SELECT_OPTIONS}
            value={booleanRadioValue(formData.isParticipatedToStudies)}
            onValueChange={(v) => onChange('isParticipatedToStudies', v)}
          />
        </div>
      </div>
    </div>
  );
}

/** Optional secondary email or phone field. */
function renderRemoteTutorSecondaryContact(params: {
  contactType: string | undefined;
  secondaryContact: string;
  countryCode: string;
  fieldError: string | undefined;
  onChange: (key: string, value: unknown) => void;
  onBlur?: (field: string) => void;
}): ReactNode {
  if (!params.contactType || params.contactType === 'unknown') return null;
  /** Persist secondary contact value. */
  function handleChange(event: ChangeEvent<HTMLInputElement>): void {
    params.onChange('secondaryContact', event.target.value);
  }
  /** Mark secondary contact touched. */
  function handleBlur(): void {
    params.onBlur?.('secondaryContact');
  }
  /** Persist secondary-phone country ISO. */
  function handleCountryChange(country: { code: string }): void {
    params.onChange('secondaryContactCountryCode', country.code);
  }
  if (params.contactType === 'email') {
    return (
      <div className="transition-all duration-300 animate-in fade-in slide-in-from-top-2">
        <PhoneInput
          label="Téléphone (contact secondaire, optionnel)"
          value={params.secondaryContact}
          onChange={handleChange}
          onBlur={handleBlur}
          countryCode={params.countryCode}
          onCountryChange={handleCountryChange}
          inputSize="sm"
          state={params.fieldError ? 'error' : 'default'}
          error={params.fieldError}
        />
      </div>
    );
  }
  return (
    <div className="transition-all duration-300 animate-in fade-in slide-in-from-top-2">
      <Input
        type="text"
        label="Email (contact secondaire, optionnel)"
        value={params.secondaryContact}
        onChange={handleChange}
        onBlur={handleBlur}
        icon={Mail}
        placeholder="Ex: contact@example.com"
        inputSize="sm"
        state={params.fieldError ? 'error' : 'default'}
        error={params.fieldError}
      />
    </div>
  );
}
